import { signal } from '@angular/core';
import { FormControl, AbstractControl, ValidationErrors } from '@angular/forms';

export function getInitials(name: string): string {
	if(!name || name.length === 0) return '';
  // Split the name by spaces
	const words = name.trim().split(' ').filter(word => word.length > 0);
	
  // Get the first letter of the first word
  const firstInitial = words[0][0].toUpperCase();

  // If there are multiple words, get the first letter of the second word
	if (words.length > 1) {
    const secondInitial = words[1][0].toUpperCase();
    return firstInitial + secondInitial;
	} else {
    // If it's a single word, find the next capital letter after the first letter
    const secondInitial = words[0].slice(1).match(/[A-Z]/);
    return firstInitial + (secondInitial ? secondInitial[0] : words[0][1].toUpperCase());
  }
}

export const isDefined = (obj: any) => obj!==null && obj!==undefined

/**
 * Devuelve el nombre del owner real de una cuenta cuando es una cuenta autorizada
 * (ownAccount === false). Para cuentas propias devuelve null para que el llamador
 * use el nombre del usuario logueado.
 */
export function getAccountOwnerName(bankAccount: any): string | null {
	if (bankAccount?.ownAccount === false && bankAccount?.customer) {
		const c = bankAccount.customer;
		if (c.business?.businessName) return c.business.businessName;
		if (c.person) return `${c.person.firstName || ''} ${c.person.lastName || ''}`.trim();
	}
	return null;
}

// export function formatDecimals(num: any, decimals = 3) {
// 	return Number(Number(num).toFixed(decimals))
// }

export function formatDecimals(num: any, decimals = 2) {
	const factor = Math.pow(10, decimals);
	return Math.floor(Number(num) * factor) / factor;
}

export function withDecimals(num: number | string) {
	if(typeof num=='string') num = parseFloat(num)
  return formatNumberWithThousands(parseFloat(num.toFixed(2)))
}

export function formatNumberWithThousands(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function isEmail(email) {
	const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
	return emailRegex.test(email);
}

const loadedScripts = signal<Set<string>>(new Set());

export function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (loadedScripts().has(src)) {
      resolve();
      return;
    }

    if (document.querySelector(`script[src="${src}"]`)) {
      loadedScripts.set(new Set(loadedScripts()));  
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      loadedScripts.set(new Set(loadedScripts().add(src))); 
      resolve();
    };

    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

    document.head.appendChild(script);
  });
}

export function formatDate(dateString:string) {
	const date = new Date(dateString)

	return date.toLocaleDateString('es-ES', {
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	}
	);	
}

export function showMessageWarning() {
	function showMessage(message, css) {
		setTimeout(console.log.bind(console, message, css));
	}

	const warningTitleCSS =
		'color:red; font-size:60px; font-weight: bold; -webkit-text-stroke: 1px black;';
	const warningDescCSS = 'font-size: 18px;';

	showMessage('%c¡Detente!', warningTitleCSS);
	showMessage(
		'%cEsta es una función pensada para desarrolladores. Si alguien te dijo que copies y pegues algo aquí para habilitar una función de SuperPay o "hackear" la cuenta de alguien, es una estafa y es posible que dicha persona obtenga acceso a tu cuenta de SuperPay.',
		warningDescCSS
	);
	showMessage(
		'%cVea https://es.wikipedia.org/wiki/Self-XSS para más información.',
		warningDescCSS
	);
}

/**
 * Validates keyboard input to allow only numbers and a single decimal separator
 * Limits decimal places to 2
 * Supports dot (.) comma (,) and numpad decimal key
 * Useful for numeric inputs where you want to prevent non-numeric characters
 * @param event KeyboardEvent from the input
 */
export function validateNumericInput(event: KeyboardEvent): void {
  // Allow: backspace, delete, tab, escape, enter, ctrl+A, home, end, arrows
  const allowedKeys = [
    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
  ];

  // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X (for copy/paste/select)
  if (
    (event.ctrlKey === true && ['a', 'c', 'v', 'x'].indexOf(event.key.toLowerCase()) !== -1) ||
    allowedKeys.includes(event.key)
  ) {
    return;
  }

  // Get input information
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const normalizedValue = value.replace(/,/g, '.');
  const cursorPosition = input.selectionStart || 0;
  const selectionStart = input.selectionStart || 0;
  const selectionEnd = input.selectionEnd || 0;
  const hasSelection = selectionStart !== selectionEnd;

  // Check if key is a number (0-9)
  const isNumber = /[0-9]/.test(event.key);

  // Allow dot/comma and numpad decimal key as decimal separators
  const isDecimalSeparator = event.key === '.' || event.key === ',' || event.code === 'NumpadDecimal';

  // Handle decimal separator
  if (isDecimalSeparator) {
    // Only allow one decimal separator in the entire value
    if (normalizedValue.includes('.') && !hasSelection) {
      event.preventDefault();
      return;
    }
    return;
  }

  // Handle numbers
  if (isNumber) {
    // If there's already a decimal separator, check decimal places limit
    if (normalizedValue.includes('.')) {
      const decimalIndex = normalizedValue.indexOf('.');
      const decimalPart = normalizedValue.substring(decimalIndex + 1);

      // If cursor is after the decimal separator and we already have 2 decimal places
      if (cursorPosition > decimalIndex && decimalPart.length >= 2) {
        // Allow if there's text selected (will be replaced)
        if (!hasSelection) {
          event.preventDefault();
          return;
        }
      }
    }
    return;
  }

  // Block any other key (letters, symbols, etc.)
  event.preventDefault();
}

/**
 * Limits the decimal places in an input field and updates the associated form control
 * Normalizes comma to dot to support both decimal separators
 * Ensures proper decimal formatting with a maximum of 2 decimal places
 * @param event The input event
 * @param formControl The form control to update (optional)
 * @param maxDecimalPlaces Maximum number of decimal places allowed (default: 2)
 */
export function limitInputToDecimalPlaces(
  event: Event,
  formControl?: FormControl,
  maxDecimalPlaces: number = 2
): void {
  const inputElement = event.target as HTMLInputElement;
  let value = inputElement.value;

  // Normalize commas to dots so both separators are accepted.
  if (value.includes(',')) {
    value = value.replace(/,/g, '.');
  }

  // Remove any non-numeric characters except the decimal point
  value = value.replace(/[^0-9.]/g, '');
  
  // Handle multiple decimal points (keep only the first one)
  const parts = value.split('.');
  if (parts.length > 2) {
    value = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Limit decimal places to maxDecimalPlaces (default: 2)
  if (value.includes('.')) {
    const [integerPart, decimalPart] = value.split('.');
    if (decimalPart && decimalPart.length > maxDecimalPlaces) {
      // Truncate to specified decimal places (max 2 decimals)
      value = `${integerPart}.${decimalPart.substring(0, maxDecimalPlaces)}`;
    }
  }
  
  // Update the input element with the cleaned value
  if (inputElement.value !== value) {
    inputElement.value = value;
  }
  
  // Update form control if provided
  if (formControl) {
    if (value === '') {
      // Set null for empty value
      formControl.setValue(null, { emitEvent: false });
    } else if (value === '.' || value.endsWith('.')) {
      // Don't update form control for incomplete decimal (like "11." or just ".")
      // Keep the visual value but don't set the form control yet
      // This allows users to type the decimal point
    } else {
      // Keep the form control numeric for complete numbers so existing DTO spreads
      // and numeric form consumers continue to receive a number, BUT only when the
      // number round-trips exactly to the typed text. Otherwise (e.g. ".0", ".06",
      // "0.0", "1.20") coercing to a number would re-render the input view and wipe
      // the in-progress decimal (".0" -> 0 -> "0"), making it impossible to type
      // values like ".06". In those cases keep the raw string; submit parses it.
      const numericValue = parseFloat(value);
      if (!Number.isNaN(numericValue) && String(numericValue) === value) {
        formControl.setValue(numericValue, { emitEvent: false });
      } else {
        formControl.setValue(value, { emitEvent: false });
      }
    }
  }
}

/**
 * Custom validator function to check if the provided value is a valid date
 * @param control The form control containing the date value
 * @returns ValidationErrors object with 'invalidDate' error if date is invalid, null otherwise
 */
export function isValidDate(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  
  if (!value) {
    return null; // If no date is selected, return no error (optional field)
  }
  
  const date = new Date(value);
  
  // Check if the date is valid (not Invalid Date)
  const isValid = !isNaN(date.getTime());
  
  return isValid ? null : { 'invalidDate': true };
}

/**
 * Custom validator function to check if date is greater than today
 * Used for ensuring dates like expiration dates are in the future
 * @param control The form control containing the date value
 * @returns ValidationErrors object with 'min' error if date is not greater than today, null otherwise
 */
export function dateGreaterThanToday(control: AbstractControl): ValidationErrors | null {
  const selectedDate = control.value;
  if (!selectedDate) {
    return null; // If no date is selected, return no error (optional field)
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time part to compare only dates
  
  const compareDate = new Date(selectedDate);
  compareDate.setHours(0, 0, 0, 0);
  
  return compareDate >= today ? null : { 'min': true };
}
